import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Check, X, Info, Pencil, DollarSign, Package, Clock, Star, Bookmark, AlertTriangle, HelpCircle } from 'lucide-react';
import { AIAnalysisService } from '../services/aiAnalysisService';
import { AIAnalysisResult, ValuationDecision } from '../types/aiAnalysis';
import { Timestamp } from 'firebase/firestore';

/**
 * Return the URL only if it is a safe http(s) link, else null. `example.url`
 * comes from AI/market-research output (Claude vision / Auctionet data), which
 * is untrusted external data — a `javascript:` or `data:` URL in an href would
 * execute on click. (Security audit W1)
 */
function safeHttpUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

const AIAnalysisResultsPage: React.FC = () => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const navigate = useNavigate();
  
  const [result, setResult] = useState<AIAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFullInfo, setShowFullInfo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isTemporary, setIsTemporary] = useState(false);
  const [valuationDecision, setValuationDecision] = useState<ValuationDecision | undefined>(undefined);
  const [valuationNotes, setValuationNotes] = useState<string>('');
  const [showValuationSection, setShowValuationSection] = useState(false);
  
  const aiService = new AIAnalysisService();

  // Revoke the temporary object URLs created for this analysis result when the
  // page unmounts or the result changes. cleanupTemporaryUrls was implemented
  // but never called, so each visit leaked blob URLs. (#18/#25)
  useEffect(() => {
    return () => {
      if (result) new AIAnalysisService().cleanupTemporaryUrls(result);
    };
  }, [result]);

  useEffect(() => {
    const loadResult = async () => {
      if (!analysisId) {
        setError('Inget analys-ID hittades');
        setLoading(false);
        return;
      }

      try {
        // First check for temporary result in sessionStorage
        const tempResult = sessionStorage.getItem('tempAnalysisResult');
        if (tempResult) {
          const parsedResult = JSON.parse(tempResult) as AIAnalysisResult;
          if (parsedResult.id === analysisId) {
            setResult(parsedResult);
            setIsTemporary(true);
            
            // Check if valuation section should be shown (over 1000 SEK)
            const minValue = parsedResult.analysis.estimatedValue?.min || 0;
            const maxValue = parsedResult.analysis.estimatedValue?.max || 0;
            const avgValue = (minValue + maxValue) / 2;
            setShowValuationSection(avgValue >= 1000);
            
            setLoading(false);
            return;
          }
        }

        // If not temporary, try to load from Firestore
        const analysisResult = await aiService.getAnalysisResult(analysisId);
        if (analysisResult) {
          setResult(analysisResult);
          setIsTemporary(false);
          
          // Load existing valuation data if available
          if (analysisResult.valuationDecision) {
            setValuationDecision(analysisResult.valuationDecision);
            setValuationNotes(analysisResult.valuationNotes || '');
          }
          
          // Check if valuation section should be shown (over 1000 SEK)
          const minValue = analysisResult.analysis.estimatedValue?.min || 0;
          const maxValue = analysisResult.analysis.estimatedValue?.max || 0;
          const avgValue = (minValue + maxValue) / 2;
          setShowValuationSection(avgValue >= 1000);
        } else {
          setError('Analysresultat hittades inte');
        }
      } catch (err) {
        console.error('Failed to load analysis result:', err);
        setError('Kunde inte ladda analysresultat');
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [analysisId]);

  const handleSaveAnalysis = async () => {
    if (!result || !isTemporary) return;
    
    setSaving(true);
    try {
      console.log('💾 Saving analysis to Firebase...');
      const savedResult = await aiService.saveAnalysisResult(result);
      
      // Update state with saved result
      setResult(savedResult);
      setIsTemporary(false);
      
      // Clear temporary data
      sessionStorage.removeItem('tempAnalysisResult');
      
      console.log('✅ Analysis saved successfully');
      
    } catch (err) {
      console.error('Failed to save analysis:', err);
      setError('Kunde inte spara analysen');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToItems = async () => {
    if (!result) return;
    
    // If analysis is temporary, save it first
    if (isTemporary) {
      await handleSaveAnalysis();
      if (!result) return; // If save failed
    }
    
    setSaving(true);
    try {
      // Navigate to item creation form with prefilled data
      const itemData = {
        name: result.analysis.brand && result.analysis.model 
          ? `${result.analysis.brand} ${result.analysis.model}`
          : result.analysis.category || 'Okänt föremål',
        brand: result.analysis.brand,
        model: result.analysis.model,
        year: result.analysis.year,
        category: result.analysis.category,
        condition: result.analysis.condition,
        description: result.analysis.description,
        materials: result.analysis.materials,
        dimensions: result.analysis.dimensions,
        estimatedValue: result.analysis.estimatedValue,
        photos: result.photos,
        aiAnalyzed: true,
        aiAnalysisId: result.id,
        additionalInfo: result.analysis.additionalInfo
      };

      // Store in sessionStorage to pass to item form
      sessionStorage.setItem('aiAnalysisData', JSON.stringify(itemData));
      
      // Navigate to item creation (user will need to select project)
      navigate('/projects'); // Let user select project first
      
    } catch (err) {
      console.error('Failed to add to items:', err);
      setError('Kunde inte lägga till föremål');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = () => {
    navigate('/ai-analysis');
  };

  const handleSaveValuationDecision = async (decision: ValuationDecision) => {
    if (!result) return;
    
    setSaving(true);
    try {
      // If analysis is temporary, save it first
      let updatedResult = result;
      if (isTemporary) {
        updatedResult = await aiService.saveAnalysisResult(result);
        setResult(updatedResult);
        setIsTemporary(false);
        sessionStorage.removeItem('tempAnalysisResult');
      }

      // Update with valuation decision
      const updatedAnalysis = {
        ...updatedResult,
        valuationDecision: decision,
        valuationNotes: valuationNotes,
        valuationDecidedAt: Timestamp.now(),
        valuationDecidedBy: 'Valuator' // In production, use actual user ID/name
      };

      const savedResult = await aiService.updateAnalysisResult(updatedResult.id, {
        valuationDecision: decision,
        valuationNotes: valuationNotes,
        valuationDecidedAt: Timestamp.now(),
        valuationDecidedBy: 'Valuator'
      });

      setResult(savedResult);
      setValuationDecision(decision);
      
      console.log('✅ Valuation decision saved:', decision);
      
    } catch (err) {
      console.error('Failed to save valuation decision:', err);
      setError('Kunde inte spara värderingsbeslut');
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'text-emerald-600';
    if (confidence >= 0.6) return 'text-amber-600';
    return 'text-red-600';
  };

  const getConfidenceText = (confidence: number): string => {
    if (confidence >= 0.8) return 'Hög säkerhet';
    if (confidence >= 0.6) return 'Medelhög säkerhet';
    return 'Låg säkerhet';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent-600 border-t-transparent mx-auto mb-2"></div>
          <p className="text-sm text-[var(--text-secondary)]">Laddar analysresultat...</p>
        </div>
      </div>
    );
  }

  if (error || !result) {
    return (
      <div>
        <div className="bg-[var(--bg-surface)] border-b border-[var(--border-default)]/80 px-3 py-2">
          <button
            onClick={() => navigate('/ai-analysis')}
            className="flex items-center p-1.5 -ml-1 min-h-[36px] min-w-[36px] text-accent-600 hover:text-accent-700"
          >
            <ArrowLeft size={16} className="mr-1" />
            <span className="text-sm font-medium">Tillbaka</span>
          </button>
        </div>
        
        <div className="p-3 text-center">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <X size={32} className="text-red-400 mx-auto mb-2" />
            <h2 className="text-sm font-medium text-red-800 mb-2">
              Kunde inte ladda resultat
            </h2>
            <p className="text-sm text-red-600 mb-3">
              {error || 'Analysresultatet kunde inte hittas'}
            </p>
            <button
              onClick={handleRetry}
              className="bg-accent-600 hover:bg-accent-700 text-white text-sm font-medium py-2 px-4 rounded-xl"
            >
              Försök igen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-default)]/80 sticky top-header z-20">
        <div className="flex items-center justify-between px-3 py-2">
          <button
            onClick={() => navigate('/ai-analysis')}
            className="flex items-center p-1.5 -ml-1 min-h-[36px] min-w-[36px] text-accent-600 hover:text-accent-700"
          >
            <ArrowLeft size={16} className="mr-1" />
            <span className="text-sm font-medium">Tillbaka</span>
          </button>
          
          <h1 className="text-sm font-semibold text-[var(--text-primary)]">
            Analysresultat
          </h1>
          
          <button
            onClick={() => setShowFullInfo(!showFullInfo)}
            aria-label="Visa information om analysen"
            aria-expanded={showFullInfo}
            className="p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] rounded-full"
          >
            <Info size={16} />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-4">
        {/* Analysis Photos */}
        <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
          <h2 className="text-sm font-medium text-[var(--text-primary)] mb-3">
            Analyserade foton
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {result.photos.map((photoUrl, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={photoUrl}
                  alt={`Analyserat foto ${index + 1}`}
                  className="w-full h-full object-cover rounded border border-[var(--border-default)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Main Analysis Results */}
        <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-[var(--text-primary)]">
              AI-identifiering
            </h2>
            <div className="flex items-center space-x-2">
              <Star size={16} className={getConfidenceColor(result.confidence)} />
              <span className={`text-sm font-medium ${getConfidenceColor(result.confidence)}`}>
                {getConfidenceText(result.confidence)}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Brand & Model */}
            {(result.analysis.brand || result.analysis.model) && (
              <div className="flex items-center space-x-2">
                <Package size={16} className="text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Märke & Modell</p>
                  <p className="font-medium">
                    {result.analysis.brand && result.analysis.model
                      ? `${result.analysis.brand} ${result.analysis.model}`
                      : result.analysis.brand || result.analysis.model
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Category */}
            {result.analysis.category && (
              <div className="flex items-center space-x-2">
                <Package size={16} className="text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Kategori</p>
                  <p className="font-medium">{result.analysis.category}</p>
                </div>
              </div>
            )}

            {/* Year */}
            {result.analysis.year && (
              <div className="flex items-center space-x-3">
                <Clock size={20} className="text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">År</p>
                  <p className="font-medium">{result.analysis.year}</p>
                </div>
              </div>
            )}

            {/* Estimated Value */}
            {result.analysis.estimatedValue && (
              <div className="flex items-center space-x-3">
                <DollarSign size={20} className="text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Uppskattat värde</p>
                  <p className="font-medium">
                    {result.analysis.estimatedValue.min} - {result.analysis.estimatedValue.max} {result.analysis.estimatedValue.currency}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Säkerhet: {Math.round(result.analysis.estimatedValue.confidence * 100)}%
                  </p>
                </div>
              </div>
            )}

            {/* Condition */}
            {result.analysis.condition && (
              <div className="flex items-center space-x-2">
                <Check size={16} className="text-[var(--text-tertiary)]" />
                <div>
                  <p className="text-sm text-[var(--text-secondary)]">Skick</p>
                  <p className="font-medium">{result.analysis.condition}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {result.analysis.description && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-2">
              Beskrivning
            </h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {result.analysis.description}
            </p>
          </div>
        )}

        {/* Similar Examples for Trustworthiness */}
        {result.analysis.similarExamples && result.analysis.similarExamples.length > 0 && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-6">
            <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4 flex items-center">
              <Check size={20} className="text-emerald-500 mr-2" />
              Liknande föremål
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              För att öka trovärdigheten har vi hittat liknande föremål från auktioner:
            </p>
            <div className="space-y-3">
              {result.analysis.similarExamples.map((example: any, index: number) => (
                <div key={index} className="border border-[var(--border-default)] rounded-lg p-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="text-sm font-medium text-[var(--text-primary)]">{example.title}</h4>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {example.source}
                    </span>
                  </div>
                  
                  {example.description && (
                    <p className="text-sm text-[var(--text-secondary)] mb-2">{example.description}</p>
                  )}
                  
                  <div className="flex justify-between items-center text-sm">
                    <div>
                      {example.soldPrice && (
                        <span className="text-emerald-600 font-medium">
                          Sålt för: {example.soldPrice.amount} {example.soldPrice.currency}
                          {example.soldPrice.date && ` (${example.soldPrice.date})`}
                        </span>
                      )}
                      {example.estimatedPrice && !example.soldPrice && (
                        <span className="text-blue-600">
                          Uppskattat: {example.estimatedPrice.min}-{example.estimatedPrice.max} {example.estimatedPrice.currency}
                        </span>
                      )}
                    </div>
                    
                    {safeHttpUrl(example.url) && (
                      <a
                        href={safeHttpUrl(example.url)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 underline"
                      >
                        Se exempel
                      </a>
                    )}
                  </div>
                  
                  <div className="mt-2">
                    <div className="flex items-center">
                      <span className="text-xs text-[var(--text-secondary)] mr-2">Likhet:</span>
                      <div className="flex-1 bg-[var(--bg-surface-alt)] rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${example.similarity * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-[var(--text-secondary)] ml-2">
                        {Math.round(example.similarity * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Market Research Summary */}
        {result.analysis.marketResearch && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center">
              <Info size={16} className="text-blue-500 mr-2" />
              Marknadsanalys
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Totalt hittade resultat</p>
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  {result.analysis.marketResearch.totalResultsFound}
                </p>
              </div>
              
              {result.analysis.marketResearch.priceRange && (
                <div>
                  <p className="text-xs text-[var(--text-secondary)]">Prisintervall från marknaden</p>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {result.analysis.marketResearch.priceRange.min} - {result.analysis.marketResearch.priceRange.max} {result.analysis.marketResearch.priceRange.currency}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Baserat på {result.analysis.marketResearch.priceRange.sampleSize} prispunkter
                  </p>
                </div>
              )}
            </div>
            
            {result.analysis.marketResearch.sources.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-[var(--text-secondary)] mb-2">Källor:</p>
                <div className="flex flex-wrap gap-2">
                  {result.analysis.marketResearch.sources.map((source: string, index: number) => (
                    <span 
                      key={index}
                      className="bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] px-2 py-1 rounded text-xs"
                    >
                      {source}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <p className="text-xs text-[var(--text-secondary)] mt-4">
              Senast uppdaterad: {new Date(result.analysis.marketResearch.lastUpdated).toLocaleDateString('sv-SE')}
            </p>
          </div>
        )}

        {/* Confidence Factors */}
        {result.analysis.confidenceFactors && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center">
              <Star size={16} className="text-amber-500 mr-2" />
              Säkerhetsfaktorer
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Bildkvalitet</span>
                <div className="flex items-center">
                  <div className="w-20 bg-[var(--bg-surface-alt)] rounded-full h-2 mr-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${result.analysis.confidenceFactors.visualClarity * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {Math.round(result.analysis.confidenceFactors.visualClarity * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Märkesidentifiering</span>
                <div className="flex items-center">
                  <div className="w-20 bg-[var(--bg-surface-alt)] rounded-full h-2 mr-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full" 
                      style={{ width: `${result.analysis.confidenceFactors.brandIdentification * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {Math.round(result.analysis.confidenceFactors.brandIdentification * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Modellidentifiering</span>
                <div className="flex items-center">
                  <div className="w-20 bg-[var(--bg-surface-alt)] rounded-full h-2 mr-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full" 
                      style={{ width: `${result.analysis.confidenceFactors.modelIdentification * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {Math.round(result.analysis.confidenceFactors.modelIdentification * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--text-secondary)]">Marknadsdata</span>
                <div className="flex items-center">
                  <div className="w-20 bg-[var(--bg-surface-alt)] rounded-full h-2 mr-2">
                    <div 
                      className="bg-violet-500 h-2 rounded-full" 
                      style={{ width: `${result.analysis.confidenceFactors.marketDataAvailable * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">
                    {Math.round(result.analysis.confidenceFactors.marketDataAvailable * 100)}%
                  </span>
                </div>
              </div>
              
              <div className="border-t pt-2 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[var(--text-primary)]">Total säkerhet</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-[var(--bg-surface-alt)] rounded-full h-2 mr-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full" 
                        style={{ width: `${result.analysis.confidenceFactors.overallFactorScore * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-bold">
                      {Math.round(result.analysis.confidenceFactors.overallFactorScore * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Details */}
        {showFullInfo && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
            <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">
              Ytterligare detaljer
            </h3>
            <div className="space-y-3">
              {result.analysis.materials && result.analysis.materials.length > 0 && (
                <div>
                  <p className="text-sm text-[var(--text-secondary)] mb-1">Material</p>
                  <div className="flex flex-wrap gap-2">
                    {result.analysis.materials.map((material, index) => (
                      <span
                        key={index}
                        className="bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] px-2 py-1 rounded text-sm"
                      >
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {result.analysis.dimensions && (
                <div>
                  <p className="text-sm text-[var(--text-secondary)] mb-1">Dimensioner</p>
                  <p className="text-[var(--text-secondary)]">{result.analysis.dimensions}</p>
                </div>
              )}

              {result.analysis.additionalInfo && result.analysis.additionalInfo.length > 0 && (
                <div>
                  <p className="text-sm text-[var(--text-secondary)] mb-1">Övrig information</p>
                  <ul className="text-[var(--text-secondary)] space-y-1">
                    {result.analysis.additionalInfo.map((info, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[var(--text-tertiary)] mr-2">•</span>
                        <span>{info}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Valuation Decision - for items over 1000 SEK */}
        {showValuationSection && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
            <div className="flex items-center mb-3">
              <DollarSign size={20} className="text-emerald-600 mr-2" />
              <h3 className="text-sm font-medium text-[var(--text-primary)]">
                Värderingsbeslut
              </h3>
              <span className="ml-2 bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-1 rounded-full">
                Över 1000 SEK
              </span>
            </div>
            
            <p className="text-[var(--text-secondary)] mb-4 text-sm">
              Detta föremål har ett uppskattat värde över 1000 SEK. Välj vad som ska göras:
            </p>

            {/* Decision Buttons */}
            <div className="grid grid-cols-1 gap-2 mb-3">
              <button
                onClick={() => handleSaveValuationDecision('take_to_shop')}
                disabled={saving}
                className={`flex items-center justify-center p-3 rounded border transition-all ${
                  valuationDecision === 'take_to_shop'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:border-emerald-300 hover:bg-emerald-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mr-2">
                    <Check size={16} className="text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">Ta med till butik</div>
                    <div className="text-xs text-[var(--text-secondary)]">Värdefull artikel, ska säljas</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleSaveValuationDecision('not_interested')}
                disabled={saving}
                className={`flex items-center justify-center p-3 rounded border transition-all ${
                  valuationDecision === 'not_interested'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:border-red-300 hover:bg-red-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mr-2">
                    <X size={16} className="text-red-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">Inte intresserad</div>
                    <div className="text-xs text-[var(--text-secondary)]">Föremålet är inte av intresse</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleSaveValuationDecision('not_sure')}
                disabled={saving}
                className={`flex items-center justify-center p-3 rounded border transition-all ${
                  valuationDecision === 'not_sure'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:border-amber-300 hover:bg-amber-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center mr-2">
                    <HelpCircle size={16} className="text-amber-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">Osäker</div>
                    <div className="text-xs text-[var(--text-secondary)]">Behöver ytterligare bedömning</div>
                  </div>
                </div>
              </button>
            </div>

            {/* Notes Input */}
            <div className="mb-3">
              <label htmlFor="valuationNotes" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                Anteckningar (valfritt)
              </label>
              <textarea
                id="valuationNotes"
                rows={3}
                value={valuationNotes}
                onChange={(e) => setValuationNotes(e.target.value)}
                placeholder="Lägg till eventuella anteckningar om värderingen..."
                className="w-full px-2 py-1.5 text-sm border border-[var(--border-default)] rounded focus:ring-2 focus:ring-accent-500 focus:border-accent-500 resize-none"
              />
            </div>

            {/* Decision Status */}
            {valuationDecision && (
              <div className="bg-[var(--bg-primary)] rounded p-2">
                <div className="flex items-center">
                  <Check size={16} className="text-emerald-600 mr-2" />
                  <span className="text-sm text-[var(--text-secondary)]">
                    Beslut sparat: <strong>
                      {valuationDecision === 'take_to_shop' && 'Ta med till butik'}
                      {valuationDecision === 'not_interested' && 'Inte intresserad'}
                      {valuationDecision === 'not_sure' && 'Osäker'}
                    </strong>
                  </span>
                </div>
                {result.valuationDecidedAt && (
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    Beslutat: {(result.valuationDecidedAt as any)?.toDate ? 
                      (result.valuationDecidedAt as any).toDate().toLocaleString('sv-SE') : 
                      new Date(result.valuationDecidedAt as any).toLocaleString('sv-SE')
                    }
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
          <div className="space-y-2">
            {/* Save Analysis Button - only for temporary results */}
            {isTemporary && (
              <button
                onClick={handleSaveAnalysis}
                disabled={saving}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-on-surface-tertiary disabled:cursor-not-allowed text-white text-sm font-semibold py-2 px-3 rounded flex items-center justify-center space-x-2 transition-colors"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sparar...</span>
                  </>
                ) : (
                  <>
                    <Bookmark size={16} />
                    <span>Spara analys</span>
                  </>
                )}
              </button>
            )}

            {/* Temporary result notice */}
            {isTemporary && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="flex items-center">
                  <AlertTriangle size={16} className="text-amber-600 mr-2" />
                  <p className="text-sm text-amber-800">
                    Detta är en tillfällig analys. Spara den för att behålla resultatet permanent.
                  </p>
                </div>
              </div>
            )}
            
            <button
              onClick={handleAddToItems}
              disabled={saving}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-on-surface-tertiary disabled:cursor-not-allowed text-white text-sm font-semibold py-2 px-3 rounded flex items-center justify-center space-x-2 transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isTemporary ? 'Sparar och lägger till...' : 'Lägger till...'}</span>
                </>
              ) : (
                <>
                  <Plus size={16} />
                  <span>Lägg till som föremål</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleRetry}
              className="w-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] text-sm font-medium py-1.5 px-3 rounded flex items-center justify-center space-x-2 transition-colors"
            >
              <Pencil size={16} />
              <span>Analysera ny artikel</span>
            </button>
          </div>
        </div>

        {/* Analysis Metadata */}
        <div className="bg-[var(--bg-surface-alt)] rounded-xl p-3">
          <div className="text-center text-xs text-[var(--text-secondary)] space-y-1">
            <p>Analyserad: {(result.analyzedAt as any)?.toDate ? (result.analyzedAt as any).toDate().toLocaleString('sv-SE') : new Date(result.analyzedAt as any).toLocaleString('sv-SE')}</p>
            <p>Analys-ID: {result.id}</p>
            <p>Säkerhetsnivå: {Math.round(result.confidence * 100)}%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisResultsPage; 